import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCT_UNIT, PRODUCT_UNIT_KIND_LABELS, ProductUnitKindValues, } from '../types/product-unit.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductUnit (0118 / spec 0050) — packaging and measure factors.
 * Stored in 'org/{orgId}/product-unit'. Firestore like Product.
 */
export const productUnitDescriptor = {
    name: PRODUCT_UNIT,
    label: 'Unidades do produto',
    domain: 'catalog',
    collectionPath: 'org/{orgId}/product-unit',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Product-unit conversion factor (packaging or measure). Border UI converts to base units; ledger stays base-only.',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'FK Product',
        },
        {
            name: 'description',
            example: 'Metro quadrado',
            label: 'Descrição',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
        },
        {
            name: 'kind',
            example: 'measure',
            label: 'Tipo',
            type: 'enum',
            required: false,
            enumValues: [...ProductUnitKindValues],
            enumLabels: PRODUCT_UNIT_KIND_LABELS,
            description: 'packaging (default when absent) or measure — request 0283 / spec 0050',
            defaultValue: 'packaging',
        },
        {
            name: 'factor',
            example: 0.147,
            label: 'Fator (un. base)',
            type: 'number',
            required: true,
            description: 'Base units per product unit (> 0)',
        },
        {
            name: 'isDerived',
            example: true,
            label: 'Calcular pelo cadastro do produto',
            type: 'boolean',
            required: false,
            description: 'Measure only — factor from grammage / entryWidth / unitWeight',
        },
        {
            name: 'quantityDecimals',
            example: 2,
            label: 'Casas decimais (exibição)',
            type: 'number',
            required: false,
            description: 'Decimal places when displaying a quantity in this unit (0–4)',
        },
        {
            name: 'unitCode',
            example: 'M2',
            label: 'Unidade',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.UNIT,
            description: 'Commercial unit code represented by this product unit (e.g. CX, RL, M2)',
        },
        {
            name: 'barcode',
            example: '7891234567901',
            label: 'Código de barras',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.BARCODE,
            // Identifica o item (GTIN) — não se copia; unicidade não é aplicada hoje.
            copy: false,
            description: 'GTIN of the product unit (EAN-14 / DUN-14) — packaging only',
        },
        {
            name: 'standardPackagingId',
            label: 'Embalagem padrão',
            type: 'string',
            required: false,
            description: 'Optional FK StandardPackaging — packaging only; prefill base, not a live sync',
        },
        {
            name: 'isDefault',
            example: false,
            label: 'Padrão',
            type: 'boolean',
            required: false,
            description: 'Default packaging for the product (one active per product)',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
        },
        {
            fieldName: 'standardPackagingId',
            targetEntity: 'StandardPackaging',
            relationship: 'many-to-one',
            displayField: 'description',
        },
    ],
};
//# sourceMappingURL=product-unit.descriptor.js.map